/** Renders a page from its stored content blocks.
 *
 * Pages are an ordered list of typed blocks (hero, heading, text, blurb,
 * image, list). The hand-built pages read their blocks by position; this
 * generic renderer is what the legal pages and any page without a dedicated
 * component use.
 */

import type { ContentBlock } from '../lib/api'
import type { ReactNode } from 'react'
import { BlurbCard, Eyebrow, FramedImage, RichText, Row, Section, SectionHeading } from './ui'

/** A text block may begin with a short label line followed by a heading.
 *  Detect that shape so the label renders as an eyebrow rather than body copy. */
function splitEyebrow(text: string): { eyebrow: string | null; rest: string } {
  const lines = text.split('\n')
  if (lines.length >= 2 && lines[0].length <= 24 && lines[0] === lines[0].trimEnd()) {
    const first = lines[0].trim()
    const looksLikeLabel = first.length > 0 && first.split(' ').length <= 3
    if (looksLikeLabel) {
      return { eyebrow: first, rest: lines.slice(1).join('\n').trim() }
    }
  }
  return { eyebrow: null, rest: text }
}

function Hero({ block }: { block: ContentBlock }) {
  return (
    <section className="bg-night px-6 py-20 text-paper md:px-10 md:py-28" data-testid="hero">
      <div className="mx-auto w-full max-w-[1200px]">
        <div className="max-w-[760px] text-left">
          {block.eyebrow ? (
            <h1 className="eyebrow m-0" data-testid="hero-eyebrow">
              {block.eyebrow}
            </h1>
          ) : null}
          {block.headline ? (
            <p
              className="m-0 mt-5 font-serif text-[clamp(40px,6vw,72px)] font-medium leading-[1.05] text-paper"
              data-testid="hero-headline"
            >
              {block.headline}
            </p>
          ) : null}
          {block.cta ? (
            <a
              href="#content"
              className="mt-9 inline-flex cursor-pointer items-center rounded-[2px] bg-paper px-[24px] py-[14px] font-sans text-[15px] font-semibold leading-none text-ink transition-colors hover:bg-amber"
              data-testid="hero-cta"
            >
              {block.cta}
            </a>
          ) : null}
        </div>
      </div>
    </section>
  )
}

/** URLs and e-mail addresses inside legal text become clickable - the copy
 *  keeps them as plain words, the page links them. */
function linkify(text: string): ReactNode[] {
  const parts = text.split(/(https?:\/\/[^\s,;)]+|[\w.+-]+@[\w-]+\.[\w.-]+)/g)
  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={index} href={part} target="_blank" rel="noreferrer noopener" className="underline">
          {part}
        </a>
      )
    }
    if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(part)) {
      return (
        <a key={index} href={`mailto:${part}`} className="underline">
          {part}
        </a>
      )
    }
    return <span key={index}>{part}</span>
  })
}

/** One paragraph run: blank line = new paragraph, single line break = <br>. */
function LegalParagraphs({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n\s*\n/).map((paragraph, index) => (
        <p key={index} className="mb-[1em]">
          {paragraph.split('\n').map((line, i) => (
            <span key={i}>
              {i > 0 ? <br /> : null}
              {linkify(line)}
            </span>
          ))}
        </p>
      ))}
    </>
  )
}

/** Impressum/Datenschutz: headings, prose and bullet lists in one flow, so
 *  the page reads like the document it is instead of stacked sections. */
function LegalDoc({ blocks }: { blocks: ContentBlock[] }) {
  return (
    <Section className="!py-14">
      <Row>
        <div className="max-w-[820px] font-sans text-[18px] leading-[1.65] text-ink">
          {blocks.map((block, index) => {
            if (block.type === 'heading') {
              const level = block.level ?? 2
              if (level <= 2) {
                return (
                  <h2
                    key={index}
                    className="mb-4 mt-12 font-serif text-[clamp(30px,3.6vw,40px)] font-medium leading-[1.15] first:mt-0"
                  >
                    {block.text}
                  </h2>
                )
              }
              if (level === 3) {
                return (
                  <h3
                    key={index}
                    className="mb-2 mt-9 font-serif text-[28px] font-medium leading-[1.2] first:mt-0"
                  >
                    {block.text}
                  </h3>
                )
              }
              return (
                <h4
                  key={index}
                  className="mb-1 mt-7 font-serif text-[22px] font-semibold leading-[1.3] first:mt-0"
                >
                  {block.text}
                </h4>
              )
            }
            if (block.type === 'list') {
              return (
                <ul key={index} className="mb-[1em] list-disc pl-6">
                  {(block.items ?? []).map((item, i) => (
                    <li key={i}>{linkify(item)}</li>
                  ))}
                </ul>
              )
            }
            if (block.type === 'image') {
              return (
                <img
                  key={index}
                  src={block.src}
                  alt={block.alt ?? ''}
                  loading="lazy"
                  className="mb-[1em] mt-6 block h-auto max-w-[220px]"
                />
              )
            }
            return <LegalParagraphs key={index} text={block.text ?? ''} />
          })}
        </div>
      </Row>
    </Section>
  )
}

export function BlockRenderer({ blocks }: { blocks: ContentBlock[] }) {
  const hero = blocks.find((b) => b.type === 'hero')
  const rest = blocks.filter((b) => b.type !== 'hero')

  // Blocks converted from legal HTML (they carry a heading level, a
  // plain-prose flag or list items) render as one continuous document.
  const isDocBlock = (block: ContentBlock) =>
    block.type === 'list' ||
    (block.type === 'heading' && block.level !== undefined) ||
    (block.type === 'text' && block.plain === true)

  // Consecutive blurbs form one grid row; consecutive document blocks form
  // one flowing legal document.
  const groups: (ContentBlock | ContentBlock[])[] = []
  let docRun: ContentBlock[] | null = null
  for (const block of rest) {
    if (isDocBlock(block) || (docRun && block.type === 'image')) {
      if (!docRun) {
        docRun = []
        groups.push(docRun)
      }
      docRun.push(block)
      continue
    }
    docRun = null
    const last = groups[groups.length - 1]
    if (block.type === 'blurb' && Array.isArray(last) && last.every((b) => b.type === 'blurb')) {
      last.push(block)
    } else if (block.type === 'blurb') {
      groups.push([block])
    } else {
      groups.push(block)
    }
  }

  return (
    <>
      {hero ? <Hero block={hero} /> : null}
      <div id="content">
        {groups.map((group, index) => {
          if (Array.isArray(group) && group.some((block) => block.type !== 'blurb')) {
            return <LegalDoc key={index} blocks={group} />
          }

          if (Array.isArray(group)) {
            return (
              <Section key={index}>
                <Row>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                    {group.map((blurb, i) => (
                      <BlurbCard
                        key={i}
                        filename={blurb.image ?? ''}
                        alt={blurb.alt ?? ''}
                        title={blurb.title ?? ''}
                        description={blurb.description}
                      />
                    ))}
                  </div>
                </Row>
              </Section>
            )
          }

          switch (group.type) {
            case 'heading':
              return (
                <Section key={index} className="!py-8">
                  <Row>
                    <SectionHeading>{group.text}</SectionHeading>
                  </Row>
                </Section>
              )

            case 'image':
              return (
                <Section key={index}>
                  <Row>
                    <FramedImage
                      filename={group.src ?? ''}
                      alt={group.alt ?? ''}
                      sizes="(max-width: 980px) 100vw, 1200px"
                    />
                  </Row>
                </Section>
              )

            case 'text': {
              const { eyebrow, rest: body } = splitEyebrow(group.text ?? '')
              return (
                <Section key={index} className="!py-8">
                  <Row>
                    {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
                    {group.html ? (
                      <RichText html={group.html} />
                    ) : (
                      <div className="font-sans text-[18px] leading-[1.65] text-ink">
                        {body.split('\n').map((line, i) => (
                          <p key={i} className="m-0 mb-[1em] last:mb-0">
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                  </Row>
                </Section>
              )
            }

            default:
              return null
          }
        })}
      </div>
    </>
  )
}
